<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div>
    <div class="honor-intro" style="margin-bottom: 24px; padding: 16px; background-color: #f5f7fa; border-radius: 4px;">
      <h3 style="margin: 0 0 12px 0; color: #303133;">External Honors</h3>
      <p style="margin: 0; color: #606266; line-height: 1.6;">
        This module is designed for <strong>applying for and managing external recognitions</strong> that your club receives from outside organizations, competitions, or institutions. Submit applications for honors, awards, certifications, or official recognitions from external bodies. All submissions must include the academic year and will be marked as "Pending Review" requiring administrator approval before being displayed on your club profile. This ensures proper verification of external achievements.
      </p>
    </div>
    <div class="flex justify-end">
      <el-button type="primary" size="small" @click="openNewHonorDialog">
        + New Honor Application
      </el-button>
    </div>

    <empty-state
      v-if="!honorList || honorList.length === 0"
      message="No honors yet"
      description="Click 'New Honor Application' to apply for your first club honor!"
      style="margin-top: 20px"
    ></empty-state>

    <el-table
      v-else
      :data="honorList"
      style="margin-top: 20px"
      :highlight-current-row="false"
      :highlight-selection-row="false"
      :header-cell-class-name="'custom-head-cell'"
      :row-class-name="'custom-row-bg'"
    >
      <el-table-column prop="title" label="Honor">
      </el-table-column>
      <el-table-column prop="academic_year" label="Academic Year" width="140">
        <template slot-scope="scope">
          {{ scope.row.academic_year || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="Status" width="160">
        <template slot-scope="scope">
          <el-tag :type="getStatusTag(scope.row.status)">{{ formatStatus(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Attachments" width="120px">
        <template slot-scope="scope">
          <div v-if="scope.row.attachments" style="font-size: 12px; color: #909399;">
            <div v-if="scope.row.attachments.events && scope.row.attachments.events.length > 0">
              <i class="el-icon-tickets"></i> {{ scope.row.attachments.events.length }} Events
            </div>
            <div v-if="scope.row.attachments.posts && scope.row.attachments.posts.length > 0">
              <i class="el-icon-document"></i> {{ scope.row.attachments.posts.length }} Posts
            </div>
            <div v-if="scope.row.attachments.documents && scope.row.attachments.documents.length > 0">
              <i class="el-icon-folder"></i> {{ scope.row.attachments.documents.length }} Docs
            </div>
          </div>
          <span v-else style="color: #dcdfe6;">-</span>
        </template>
      </el-table-column>
      <el-table-column label="Operation" width="200px">
        <template slot-scope="scope">
          <template v-if="scope.row.status === 'verified'">
            <el-button type="primary" size="small" @click="viewHonorCertificate(scope.row)">
              <i class="el-icon-view"></i> View Honor
            </el-button>
          </template>
          <template v-else>
            <el-button type="text" size="small" @click="handleEdit(scope.row)">
              Edit
            </el-button>
            <el-button type="text" size="small" @click="viewHonorCertificate(scope.row)" style="color: #409EFF;">
              View
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="handleDelete(scope.row)"
            >
              Delete
            </el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- Honor Dialog -->
    <el-dialog
      :title="isEditing ? 'Edit Honor' : 'New Honor Application'"
      :visible.sync="dialogVisible"
      width="800px"
      :close-on-click-modal="false"
    >
      <el-form :model="honorForm" :rules="rules" ref="honorForm" label-width="140px">
        <el-form-item label="Honor" prop="honor_id">
          <el-select v-model="honorForm.honor_id" placeholder="Select honor" style="width: 100%" @change="handleHonorChange">
            <el-option
              v-for="honor in standardHonors"
              :key="honor._id.$id"
              :label="honor.title"
              :value="honor._id.$id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Academic Year" prop="academic_year">
          <el-select
            v-model="honorForm.academic_year"
            placeholder="Select academic year"
            style="width: 100%"
          >
            <el-option
              v-for="year in academicYears"
              :key="year.value"
              :label="year.label"
              :value="year.value"
            >
              <span>{{ year.label }}</span>
              <span v-if="year.isCurrent" style="float: right; color: #409EFF; font-size: 12px; margin-left: 10px;">Current</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input
            type="textarea"
            :rows="4"
            v-model="honorForm.description"
            placeholder="Describe the honor, achievement, or recognition"
          ></el-input>
        </el-form-item>

        <el-divider>Attach Supporting Evidence</el-divider>

        <el-alert
          title="Attach relevant club activities as evidence for this honor application"
          type="info"
          :closable="false"
          style="margin-bottom: 20px"
        >
        </el-alert>

        <el-form-item label="Events">
          <el-select
            v-model="honorForm.attachments.events"
            multiple
            placeholder="Select relevant events"
            style="width: 100%"
            collapse-tags
          >
            <el-option
              v-for="event in availableEvents"
              :key="event._id.$id"
              :label="event.title"
              :value="event._id.$id"
            >
              <span style="float: left">{{ event.title }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ formatDate(event.start_time) }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Posts">
          <el-select
            v-model="honorForm.attachments.posts"
            multiple
            placeholder="Select relevant posts"
            style="width: 100%"
            collapse-tags
          >
            <el-option
              v-for="post in availablePosts"
              :key="post._id.$id"
              :label="post.title"
              :value="post._id.$id"
            >
              <span style="float: left">{{ post.title }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ formatDate(post.create_time) }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Documents">
          <el-select
            v-model="honorForm.attachments.documents"
            multiple
            placeholder="Select relevant documents"
            style="width: 100%"
            collapse-tags
          >
            <el-option
              v-for="doc in availableDocuments"
              :key="doc._id.$id"
              :label="doc.name"
              :value="doc._id.$id"
            >
              <span style="float: left">{{ doc.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ formatDate(doc.create_time) }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-alert
          v-if="!isEditing"
          title="Your honor application will be submitted for review"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 16px"
        >
        </el-alert>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submitHonor">
          {{ isEditing ? 'Save' : 'Submit Application' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- Honor Certificate View Dialog -->
    <el-dialog
      :visible.sync="certificateVisible"
      width="700px"
      :show-close="true"
      custom-class="certificate-dialog"
      :close-on-click-modal="true"
    >
      <div v-if="certificateData" class="certificate-wrapper">
        <!-- Certificate Frame -->
        <div class="certificate-frame" :class="{ 'verified': certificateData.status === 'verified' }">
          <!-- Header -->
          <div class="cert-header">
            <img src="@/assets/image/eclub-logo-1-theme.png" alt="eClub" class="cert-logo" />
            <h2 class="cert-title-label">Certificate of Honor</h2>
            <div class="cert-status-row">
              <div class="cert-badge" v-if="certificateData.status === 'verified'">
                <i class="el-icon-success"></i>
              </div>
              <div class="cert-badge pending" v-else-if="certificateData.status === 'pending'">
                <i class="el-icon-time"></i>
              </div>
              <div class="cert-badge revision" v-else-if="certificateData.status === 'revision_required'">
                <i class="el-icon-warning"></i>
              </div>
              <div class="cert-status-label">
                <el-tag :type="getStatusTag(certificateData.status)" size="medium" effect="dark">
                  {{ formatStatus(certificateData.status) }}
                </el-tag>
              </div>
            </div>
          </div>

          <!-- Honor Title -->
          <div class="cert-honor-title">{{ certificateData.title }}</div>

          <!-- Details -->
          <div class="cert-details">
            <div class="cert-detail-row" v-if="certificateData.club_name">
              <span class="cert-label">Club</span>
              <span class="cert-value">{{ certificateData.club_name }}</span>
            </div>
            <div class="cert-detail-row" v-if="certificateData.account_name">
              <span class="cert-label">School</span>
              <span class="cert-value">{{ certificateData.account_name }}</span>
            </div>
            <div class="cert-detail-row">
              <span class="cert-label">Academic Year</span>
              <span class="cert-value">{{ certificateData.academic_year || '-' }}</span>
            </div>
            <div class="cert-detail-row" v-if="certificateData.description">
              <span class="cert-label">Description</span>
              <span class="cert-value" style="white-space: pre-wrap;">{{ certificateData.description }}</span>
            </div>
            <div class="cert-detail-row">
              <span class="cert-label">Submitted By</span>
              <span class="cert-value">{{ certificateData.creator_name || '-' }}</span>
            </div>
            <div class="cert-detail-row" v-if="certificateData.review_time">
              <span class="cert-label">Reviewed On</span>
              <span class="cert-value">{{ formatFullDate(certificateData.review_time) }}</span>
            </div>
            <div class="cert-detail-row" v-if="certificateData._id">
              <span class="cert-label">Honor ID</span>
              <span class="cert-value" style="font-family: monospace; font-size: 12px; color: #909399;">{{ certificateData._id.$id || certificateData._id }}</span>
            </div>
          </div>

          <!-- Attachments Summary -->
          <div class="cert-attachments" v-if="certificateData.attachments">
            <div class="cert-section-title">Supporting Evidence</div>
            <div class="cert-attachment-grid">
              <div v-if="certificateData.attachments.events && certificateData.attachments.events.length > 0" class="cert-attach-group">
                <div class="cert-attach-label"><i class="el-icon-date"></i> Events</div>
                <div v-for="(event, idx) in certificateData.attachments.events" :key="'ce-'+idx" class="cert-attach-item">
                  {{ event.title || event }}
                </div>
              </div>
              <div v-if="certificateData.attachments.posts && certificateData.attachments.posts.length > 0" class="cert-attach-group">
                <div class="cert-attach-label"><i class="el-icon-chat-dot-round"></i> Posts</div>
                <div v-for="(post, idx) in certificateData.attachments.posts" :key="'cp-'+idx" class="cert-attach-item">
                  {{ post.title || post }}
                </div>
              </div>
              <div v-if="certificateData.attachments.documents && certificateData.attachments.documents.length > 0" class="cert-attach-group">
                <div class="cert-attach-label"><i class="el-icon-folder"></i> Documents</div>
                <div v-for="(doc, idx) in certificateData.attachments.documents" :key="'cd-'+idx" class="cert-attach-item">
                  <span>{{ doc.name || doc.file_name || doc }}</span>
                  <el-button v-if="doc.file_url" type="text" size="mini" @click="openUrl(doc.file_url)" style="margin-left: 8px; padding: 0;">
                    <i class="el-icon-link"></i> View
                  </el-button>
                </div>
              </div>
              <div v-if="(!certificateData.attachments.events || certificateData.attachments.events.length === 0) &&
                          (!certificateData.attachments.posts || certificateData.attachments.posts.length === 0) &&
                          (!certificateData.attachments.documents || certificateData.attachments.documents.length === 0)"
                   style="color: #c0c4cc; font-size: 13px;">
                No supporting evidence attached
              </div>
            </div>
          </div>

          <!-- Review Comments -->
          <div class="cert-comments" v-if="certificateData.review_history && certificateData.review_history.length > 0">
            <div class="cert-section-title">Committee Review Comments</div>
            <div v-for="(review, idx) in certificateData.review_history" :key="'rv-'+idx" class="cert-comment-item">
              <div class="cert-comment-header">
                <el-tag :type="getStatusTag(review.status)" size="mini">{{ formatStatus(review.status) }}</el-tag>
                <span class="cert-comment-time">{{ formatFullDate(review.time) }}</span>
              </div>
              <div v-if="review.comment" class="cert-comment-text">{{ review.comment }}</div>
              <div v-else class="cert-comment-text" style="color: #c0c4cc; font-style: italic;">No comment</div>
            </div>
          </div>

          <!-- Footer -->
          <div class="cert-footer">
            <div class="cert-footer-line"></div>
            <div class="cert-footer-text">www.eclub.center</div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" icon="el-icon-download" @click="printCertificate">Download / Print</el-button>
        <el-button @click="certificateVisible = false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: 'Honors',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({})
    },
    club_id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      honorList: [],
      standardHonors: [],
      dialogVisible: false,
      isEditing: false,
      currentUserId: getUserId(),
      certificateVisible: false,
      certificateData: null,
      certificateLoading: false,
      honorForm: {
        title: '',
        honor_id: '',
        academic_year: '',
        description: '',
        status: 'pending',
        attachments: {
          events: [],
          posts: [],
          documents: []
        }
      },
      rules: {
        honor_id: [
          { required: true, message: 'Please select honor', trigger: 'change' }
        ],
        academic_year: [
          { required: true, message: 'Please select academic year', trigger: 'change' }
        ],
        description: [
          { required: true, message: 'Please enter description', trigger: 'blur' }
        ]
      },
      availableEvents: [],
      availablePosts: [],
      availableDocuments: []
    }
  },

  computed: {
    academicYears() {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth() + 1 // 1-12

      // Academic year starts in September (month 9)
      let academicYearStart
      if (currentMonth >= 9) {
        // Sept-Dec: we're in the new academic year
        academicYearStart = currentYear
      } else {
        // Jan-Aug: we're still in the previous academic year
        academicYearStart = currentYear - 1
      }

      // Generate 5 academic years: 2 past, current, 2 future
      const years = []
      for (let i = -2; i <= 2; i++) {
        const startYear = academicYearStart + i
        const endYear = startYear + 1
        const startYearShort = String(startYear).slice(-2)
        const endYearShort = String(endYear).slice(-2)
        years.push({
          value: `${startYearShort}-${endYearShort}`,
          label: `${startYear}-${endYear} Academic Year`,
          isCurrent: i === 0
        })
      }

      return years
    },

    currentAcademicYear() {
      const current = this.academicYears.find(y => y.isCurrent)
      return current ? current.value : ''
    }
  },

  mounted() {
    this.fetchHonors()
    this.fetchStandardHonors()
    this.fetchAvailableAttachments()
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: '2-digit'
      })
    },

    formatFullDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getStatusTag(status) {
      const types = {
        'pending': 'warning',
        'verified': 'success',
        'approved': 'success',
        'revision_required': 'danger',
        'rejected': 'danger'
      }
      return types[status] || 'info'
    },

    formatStatus(status) {
      const statusMap = {
        'pending': 'Pending Review',
        'verified': 'Verified',
        'approved': 'Approved',
        'revision_required': 'Revision Required',
        'rejected': 'Rejected'
      }
      return statusMap[status] || status
    },

    openUrl(url) {
      window.open(url, '_blank')
    },

    printCertificate() {
      const printContent = document.querySelector('.certificate-wrapper')
      if (!printContent) return

      // Get the resolved logo src from the rendered img
      const logoImg = printContent.querySelector('.cert-logo')
      const logoSrc = logoImg ? logoImg.src : ''

      const printWindow = window.open('', '_blank')
      printWindow.document.write(`
        <html>
        <head>
          <title>Honor Certificate - ${this.certificateData.title || 'eClub'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { padding: 24px; background: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .certificate-wrapper { padding: 0; }
            .certificate-frame {
              background: white; border-radius: 8px; padding: 40px 36px;
              border: 3px solid #FF6450; position: relative;
              box-shadow: 0 0 0 1px #FF6450, 0 0 0 4px rgba(255, 100, 80, 0.1);
            }
            .cert-header { text-align: center; margin-bottom: 28px; }
            .cert-logo { height: 36px; margin-bottom: 16px; }
            .cert-status-row {
              display: flex; align-items: center; justify-content: center;
              gap: 10px; margin-top: 12px;
            }
            .cert-badge {
              width: 32px; height: 32px; border-radius: 50%;
              background: linear-gradient(135deg, #FF6450 0%, #FF8A65 100%);
              color: white; display: flex; align-items: center; justify-content: center;
              font-size: 16px; flex-shrink: 0;
            }
            .cert-badge.pending {
              background: linear-gradient(135deg, #e6a23c 0%, #f0a020 100%);
            }
            .cert-badge.revision {
              background: linear-gradient(135deg, #f56c6c 0%, #e64040 100%);
            }
            .cert-badge i { font-style: normal; }
            .cert-comment-text { word-break: normal; overflow-wrap: break-word; }
            .cert-status-tag {
              display: inline-block; padding: 2px 10px; border-radius: 4px;
              font-size: 12px; font-weight: 500;
              background-color: #FF6450; color: white;
            }
            .cert-status-tag.pending { background-color: #e6a23c; }
            .cert-status-tag.revision { background-color: #f56c6c; }
            .cert-status-tag.rejected { background-color: #f56c6c; }
            .cert-title-label {
              font-size: 22px; color: #333; font-weight: 300;
              letter-spacing: 2px; text-transform: uppercase; margin: 8px 0 0 0;
            }
            .cert-honor-title {
              text-align: center; font-size: 26px; font-weight: 700; color: #1a1a1a;
              padding: 16px 0 24px; border-bottom: 1px solid #eee; margin-bottom: 24px;
            }
            .cert-details { margin-bottom: 24px; }
            .cert-detail-row {
              display: flex; padding: 10px 0; border-bottom: 1px solid #f5f5f5;
            }
            .cert-detail-row:last-child { border-bottom: none; }
            .cert-label { width: 130px; flex-shrink: 0; color: #909399; font-size: 13px; font-weight: 500; }
            .cert-value { flex: 1; color: #303133; font-size: 14px; line-height: 1.5; }
            .cert-section-title {
              font-size: 14px; font-weight: 600; color: #333;
              margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #f0f0f0;
            }
            .cert-attachments { margin-bottom: 24px; }
            .cert-attachment-grid { display: flex; flex-direction: column; gap: 12px; }
            .cert-attach-group { padding: 10px 12px; background: #fafafa; border-radius: 6px; }
            .cert-attach-label { font-size: 12px; font-weight: 600; color: #606266; margin-bottom: 6px; }
            .cert-attach-item { font-size: 13px; color: #333; padding: 4px 0; }
            .cert-comments { margin-bottom: 24px; }
            .cert-comment-item {
              padding: 12px; background: #f9f9fb; border-radius: 6px;
              margin-bottom: 8px; border-left: 3px solid #dcdfe6;
            }
            .cert-comment-header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
            .cert-comment-time { font-size: 12px; color: #909399; }
            .cert-comment-text { font-size: 13px; color: #303133; line-height: 1.6; }
            .cert-footer { text-align: center; margin-top: 32px; }
            .cert-footer-line { width: 120px; height: 1px; background: #dcdfe6; margin: 0 auto 12px; }
            .cert-footer-text { font-size: 12px; color: #909399; letter-spacing: 1px; }
            .el-tag, .el-button { display: none !important; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
        </html>
      `)

      // Fix logo src (webpack-resolved URL may not carry over)
      const printLogo = printWindow.document.querySelector('.cert-logo')
      if (printLogo && logoSrc) {
        printLogo.src = logoSrc
      }

      // Replace Element UI icons in badges with Unicode characters
      const badges = printWindow.document.querySelectorAll('.cert-badge')
      badges.forEach(badge => {
        const icon = badge.querySelector('i')
        if (!icon) return
        if (badge.classList.contains('pending')) {
          icon.textContent = '\u23F3'
        } else if (badge.classList.contains('revision')) {
          icon.textContent = '\u26A0'
        } else {
          icon.textContent = '\u2713'
        }
      })

      // Replace el-tag with styled span for print
      const statusLabel = printWindow.document.querySelector('.cert-status-label')
      if (statusLabel && this.certificateData) {
        const statusClass = this.certificateData.status === 'pending' ? 'pending'
          : (this.certificateData.status === 'revision_required' || this.certificateData.status === 'rejected') ? 'revision' : ''
        statusLabel.innerHTML = '<span class="cert-status-tag ' + statusClass + '">' + this.formatStatus(this.certificateData.status) + '</span>'
      }

      // Replace review comment el-tags
      const commentHeaders = printWindow.document.querySelectorAll('.cert-comment-header')
      if (this.certificateData && this.certificateData.review_history) {
        commentHeaders.forEach((header, idx) => {
          const review = this.certificateData.review_history[idx]
          if (review) {
            const tag = header.querySelector('.el-tag')
            if (tag) {
              const span = printWindow.document.createElement('span')
              span.className = 'cert-status-tag'
              span.textContent = this.formatStatus(review.status)
              tag.replaceWith(span)
            }
          }
        })
      }

      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 300)
    },

    async viewHonorCertificate(honor) {
      this.certificateLoading = true
      this.certificateVisible = true

      try {
        const honorId = honor._id ? (honor._id.$id || honor._id) : ''
        const response = await postRequest('getClubHonor', {
          honor_id: honorId
        })

        if (response.data.code === 0) {
          this.certificateData = response.data.data
          // Add club info from clubInfo prop if not returned by API
          if (!this.certificateData.club_name && this.clubInfo && this.clubInfo.name) {
            this.certificateData.club_name = this.clubInfo.name
          }
        } else {
          // Fallback to the list data
          this.certificateData = { ...honor }
          if (!this.certificateData.club_name && this.clubInfo && this.clubInfo.name) {
            this.certificateData.club_name = this.clubInfo.name
          }
        }
      } catch (error) {
        console.error('Error fetching honor detail:', error)
        this.certificateData = { ...honor }
        if (!this.certificateData.club_name && this.clubInfo && this.clubInfo.name) {
          this.certificateData.club_name = this.clubInfo.name
        }
      } finally {
        this.certificateLoading = false
      }
    },

    openNewHonorDialog() {
      this.isEditing = false
      this.honorForm = {
        title: '',
        honor_id: '',
        academic_year: this.currentAcademicYear, // Auto-select current academic year
        description: '',
        status: 'pending',
        attachments: {
          events: [],
          posts: [],
          documents: []
        }
      }
      this.dialogVisible = true
    },

    handleEdit(honor) {
      this.isEditing = true

      // Extract honor_id properly - it might be an object with $id
      let honorId = ''
      if (honor.honor_id) {
        honorId = typeof honor.honor_id === 'object' && honor.honor_id.$id
          ? honor.honor_id.$id
          : honor.honor_id
      } else if (honor.title && this.standardHonors.length > 0) {
        // Try to find the standard honor by matching title (for old records without honor_id)
        const honorTitle = honor.title.trim()
        const matchingHonor = this.standardHonors.find(h => h.title && h.title.trim() === honorTitle)
        if (matchingHonor) {
          honorId = matchingHonor._id.$id
        }
      }

      // Extract attachments and convert any MongoDB IDs to strings
      let attachments = {
        events: [],
        posts: [],
        documents: []
      }

      if (honor.attachments) {
        if (Array.isArray(honor.attachments.events)) {
          attachments.events = honor.attachments.events.map(id =>
            typeof id === 'object' && id.$id ? id.$id : id
          )
        }
        if (Array.isArray(honor.attachments.posts)) {
          attachments.posts = honor.attachments.posts.map(id =>
            typeof id === 'object' && id.$id ? id.$id : id
          )
        }
        if (Array.isArray(honor.attachments.documents)) {
          attachments.documents = honor.attachments.documents.map(id =>
            typeof id === 'object' && id.$id ? id.$id : id
          )
        }
      }

      this.honorForm = {
        _id: honor._id,
        title: honor.title,
        honor_id: honorId,
        academic_year: honor.academic_year || this.currentAcademicYear, // Use existing or default to current
        description: honor.description,
        status: honor.status,
        attachments: attachments
      }

      this.dialogVisible = true
    },

    handleHonorChange(honorId) {
      const selectedHonor = this.standardHonors.find(h => h._id.$id === honorId)
      if (selectedHonor) {
        this.honorForm.title = selectedHonor.title
      }
    },

    async handleDelete(honor) {
      try {
        await this.$confirm('Are you sure you want to delete this honor?', 'Warning', {
          type: 'warning'
        })

        const response = await postRequest('deleteClubHonor', {
          honor_id: honor._id.$id
        })

        if (response.data.code === 0) {
          this.$message.success('Honor deleted successfully')
          this.fetchHonors()
        } else {
          this.$message.error(response.data.message || 'Failed to delete honor')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting honor:', error)
          this.$message.error('Failed to delete honor')
        }
      }
    },

    async submitHonor() {
      try {
        await this.$refs.honorForm.validate()

        const response = await postRequest('updateClubHonor', {
          id: this.isEditing && this.honorForm._id ? this.honorForm._id.$id : '',
          club_id: this.club_id,
          user_id: this.currentUserId,
          user_type: getUserType(),
          title: this.honorForm.title,
          honor_id: this.honorForm.honor_id,
          academic_year: this.honorForm.academic_year,
          description: this.honorForm.description,
          status: this.honorForm.status,
          attachments: this.honorForm.attachments
        })

        if (response.data.code === 0) {
          this.$message.success(`Honor ${this.isEditing ? 'updated' : 'application submitted'} successfully`)
          this.dialogVisible = false
          this.fetchHonors()
        } else {
          this.$message.error(response.data.message || 'Failed to save honor')
        }
      } catch (error) {
        console.error('Error saving honor:', error)
        this.$message.error('Failed to save honor')
      }
    },

    async fetchAvailableAttachments() {
      try {
        // Fetch events
        const eventsResponse = await postRequest('getClubEvents', {
          club_id: this.club_id
        })
        if (eventsResponse.data.code === 0) {
          this.availableEvents = eventsResponse.data.data
        }

        // Fetch posts
        const postsResponse = await postRequest('getClubPosts', {
          club_id: this.club_id
        })
        if (postsResponse.data.code === 0) {
          this.availablePosts = postsResponse.data.data
        }

        // Fetch documents
        const docsResponse = await postRequest('getClubDocuments', {
          club_id: this.club_id
        })
        if (docsResponse.data.code === 0) {
          this.availableDocuments = docsResponse.data.data
        }
      } catch (error) {
        console.error('Error fetching attachments:', error)
      }
    },

    async fetchStandardHonors() {
      try {
        const response = await postRequest('getClubStandardHonors', {
          club_id: this.club_id
        })

        if (response.data.code === 0) {
          this.standardHonors = response.data.data
        } else {
          this.$message.error('Failed to fetch standard honors')
        }
      } catch (error) {
        console.error('Error fetching standard honors:', error)
        this.$message.error('Failed to fetch standard honors')
      }
    },

    async fetchHonors() {
      try {
        const response = await postRequest('getClubHonors', {
          club_id: this.club_id
        })

        if (response.data.code === 0) {
          this.honorList = response.data.data
        } else {
          this.$message.error('Failed to fetch honors')
        }
      } catch (error) {
        console.error('Error fetching honors:', error)
        this.$message.error('Failed to fetch honors')
      }
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.custom-head-cell {
  background: #FF8833;
  color: white;
  box-sizing: border-box;
  font-family: GravityBold;
}

:deep(.el-table) {
  .el-button--text {
    padding: 0;
    margin-right: 16px;
  }

  .el-button--danger {
    padding: 7px 15px;
    margin-right: 16px;
    background-color: white;
    border: 1px solid #F56C6C;
    color: #F56C6C;

    &:hover {
      background-color: #F56C6C;
      color: white;
    }
  }
}

:deep(.el-divider__text) {
  background-color: #f5f7fa;
  padding: 0 16px;
  font-weight: 600;
  color: #606266;
}

:deep(.el-select) {
  .el-tag {
    max-width: 120px;
  }
}

.flex {
  display: flex;
}

.justify-end {
  justify-content: flex-end;
}

/* Certificate Dialog */
:deep(.certificate-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}

.certificate-wrapper {
  padding: 24px;
  background: #f8f9fa;
}

.certificate-frame {
  background: white;
  border-radius: 8px;
  padding: 40px 36px;
  border: 3px solid #e0e0e0;
  position: relative;

  &.verified {
    border-color: #FF6450;
    box-shadow: 0 0 0 1px #FF6450, 0 0 0 4px rgba(255, 100, 80, 0.1);
  }
}

.cert-header {
  text-align: center;
  margin-bottom: 28px;
}

.cert-logo {
  height: 36px;
  margin: 0 auto 16px;
  display: block;
}

.cert-status-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
}

.cert-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF6450 0%, #FF8A65 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(255, 100, 80, 0.3);

  &.pending {
    background: linear-gradient(135deg, #e6a23c 0%, #f0a020 100%);
    box-shadow: 0 2px 8px rgba(230, 162, 60, 0.3);
  }

  &.revision {
    background: linear-gradient(135deg, #f56c6c 0%, #e64040 100%);
    box-shadow: 0 2px 8px rgba(245, 108, 108, 0.3);
  }
}

.cert-status-label {
  :deep(.el-tag--success) {
    background-color: #FF6450;
    border-color: #FF6450;
    color: white;
  }
}

.cert-title-label {
  font-size: 22px;
  color: #333;
  font-weight: 300;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 8px 0 0 0;
}

.cert-honor-title {
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  padding: 16px 0 24px;
  border-bottom: 1px solid #eee;
  margin-bottom: 24px;
}

.cert-details {
  margin-bottom: 24px;
}

.cert-detail-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.cert-label {
  width: 130px;
  flex-shrink: 0;
  color: #909399;
  font-size: 13px;
  font-weight: 500;
}

.cert-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  line-height: 1.5;
}

.cert-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.cert-attachments {
  margin-bottom: 24px;
}

.cert-attachment-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cert-attach-group {
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 6px;
}

.cert-attach-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;

  i {
    margin-right: 4px;
  }
}

.cert-attach-item {
  font-size: 13px;
  color: #333;
  padding: 4px 0;
  display: flex;
  align-items: center;

  &::before {
    content: '·';
    margin-right: 8px;
    color: #c0c4cc;
    font-weight: bold;
  }
}

.cert-comments {
  margin-bottom: 24px;
}

.cert-comment-item {
  padding: 12px;
  background: #f9f9fb;
  border-radius: 6px;
  margin-bottom: 8px;
  border-left: 3px solid #dcdfe6;

  &:last-child {
    margin-bottom: 0;
  }
}

.cert-comment-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.cert-comment-time {
  font-size: 12px;
  color: #909399;
}

.cert-comment-text {
  font-size: 13px;
  color: #303133;
  line-height: 1.6;
  word-break: normal;
  overflow-wrap: break-word;
}

.cert-footer {
  text-align: center;
  margin-top: 32px;
}

.cert-footer-line {
  width: 120px;
  height: 1px;
  background: #dcdfe6;
  margin: 0 auto 12px;
}

.cert-footer-text {
  font-size: 12px;
  color: #909399;
  letter-spacing: 1px;
}
</style>
